import { useMemo, useState } from 'react';
import { ArrowRight, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { deleteSession } from '@/lib/api';
import { formatDuration, formatNumber } from '@/lib/utils';
import type { LiveSession } from '@/types';
import { Button } from './ui/button';
import { SessionStatusBadge } from './SessionStatusBadge';
import { cn } from '@/lib/utils';

type SessionFilter = 'all' | LiveSession['status'];

interface RecentSessionsListProps {
  sessions: LiveSession[];
  onSessionUpdated: () => void;
}

const filters: Array<{ value: SessionFilter; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'live', label: '直播中' },
  { value: 'scheduled', label: '已预定' },
  { value: 'ended', label: '已结束' },
];

function formatSessionTime(session: LiveSession): string {
  const value = session.actual_start_time ?? session.scheduled_start_time ?? session.created_at;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '时间未知';
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function RecentSessionsList({ sessions, onSessionUpdated }: RecentSessionsListProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<SessionFilter>('all');
  const [visibleCount, setVisibleCount] = useState(10);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const counts = useMemo(
    () => ({
      all: sessions.length,
      live: sessions.filter((session) => session.status === 'live').length,
      scheduled: sessions.filter((session) => session.status === 'scheduled').length,
      ended: sessions.filter((session) => session.status === 'ended').length,
    }),
    [sessions]
  );

  const filteredSessions = useMemo(
    () => sessions.filter((session) => filter === 'all' || session.status === filter),
    [filter, sessions]
  );
  const visibleSessions = filteredSessions.slice(0, visibleCount);

  const handleFilter = (nextFilter: SessionFilter) => {
    setFilter(nextFilter);
    setVisibleCount(10);
  };

  const handleDelete = async (session: LiveSession) => {
    if (!window.confirm('确定要删除这个场次吗？相关的弹幕数据也会被删除。')) return;

    try {
      setDeletingId(session.id);
      await deleteSession(session.id);
      onSessionUpdated();
    } catch (error) {
      console.error('删除场次失败:', error);
      window.alert('删除失败，请重试');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section aria-labelledby="recent-sessions-title" className="console-panel">
      <div className="console-panel-header">
        <div className="flex min-w-0 items-center gap-3">
          <h2 id="recent-sessions-title" className="text-sm font-bold text-[var(--text-ink)]">
            最近场次
          </h2>
          <div className="hidden gap-1 md:flex" aria-label="筛选直播场次">
            {filters.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => handleFilter(item.value)}
                aria-pressed={filter === item.value}
                className={cn(
                  'min-h-9 rounded px-3 text-xs transition-colors',
                  filter === item.value
                    ? 'bg-[var(--surface-selected)] font-medium text-[var(--text-ink)]'
                    : 'text-[var(--text-muted)] hover:bg-[var(--panel-raised)] hover:text-[var(--text-soft)]'
                )}
              >
                {item.label} {counts[item.value]}
              </button>
            ))}
          </div>
        </div>

        <label className="md:hidden">
          <span className="sr-only">筛选直播场次</span>
          <select
            value={filter}
            onChange={(event) => handleFilter(event.target.value as SessionFilter)}
            className="min-h-11 rounded-md border border-[var(--border-control)] bg-[var(--panel-deep)] px-3 text-xs text-[var(--text-soft)]"
          >
            {filters.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label} {counts[item.value]}
              </option>
            ))}
          </select>
        </label>

        <p className="hidden font-mono text-[11px] text-[var(--text-muted)] lg:block">
          显示最近 {Math.min(visibleSessions.length, 10)} 条
        </p>
      </div>

      {visibleSessions.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <p className="text-sm font-medium text-[var(--text-soft)]">当前筛选下没有场次</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">切换状态筛选查看其他记录</p>
        </div>
      ) : (
        <>
          <div className="hidden md:block">
            <table className="w-full table-fixed text-left">
              <thead className="border-b border-[var(--border-subtle)] font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--text-muted)]">
                <tr>
                  <th className="w-[112px] px-[18px] py-2.5 font-medium">状态</th>
                  <th className="px-3 py-2.5 font-medium">标题</th>
                  <th className="w-[180px] px-3 py-2.5 font-medium">主播 / 房间号</th>
                  <th className="w-[128px] px-3 py-2.5 font-medium">开始时间</th>
                  <th className="w-[100px] px-3 py-2.5 text-right font-medium">弹幕数</th>
                  <th className="w-[98px] px-3 py-2.5 text-right font-medium">时长</th>
                  <th className="w-[116px] px-[18px] py-2.5 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {visibleSessions.map((session) => (
                  <tr key={session.id} className="transition-colors hover:bg-[var(--surface-hover)]">
                    <td className="px-[18px] py-2.5">
                      <SessionStatusBadge status={session.status} />
                    </td>
                    <td className="min-w-0 px-3 py-2.5">
                      <p className="truncate text-[13px] text-[var(--text-row)]">
                        {session.room_title || '未命名场次'}
                      </p>
                    </td>
                    <td className="truncate px-3 py-2.5 text-xs text-[var(--text-muted)]">
                      {session.streamer_name} · {session.room_id}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-[var(--text-muted)]">
                      {formatSessionTime(session)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-[var(--text-soft)]">
                      {formatNumber(session.danmaku_count)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-[var(--text-muted)]">
                      {session.duration_seconds ? formatDuration(session.duration_seconds) : '—'}
                    </td>
                    <td className="px-[18px] py-2.5">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => navigate('/session/' + session.id)}
                          className="min-h-9 text-xs text-[var(--action-blue)] hover:text-[var(--text-ink)]"
                        >
                          详情
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(session)}
                          disabled={deletingId === session.id}
                          className="min-h-9 text-xs text-[var(--text-muted)] hover:text-[var(--status-error)] disabled:opacity-40"
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-[var(--border-subtle)] md:hidden">
            {visibleSessions.map((session) => (
              <article key={session.id} className="flex items-center gap-2.5 px-3 py-3">
                <span
                  className={cn(
                    'h-1.5 w-1.5 shrink-0 rounded-full',
                    session.status === 'live'
                      ? 'bg-[var(--status-live)]'
                      : session.status === 'scheduled'
                        ? 'bg-[var(--status-action)]'
                        : 'bg-[var(--status-unknown)]'
                  )}
                />
                <button
                  type="button"
                  onClick={() => navigate('/session/' + session.id)}
                  className="min-h-11 min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-[13px] text-[var(--text-row)]">
                    {session.room_title || '未命名场次'}
                  </span>
                  <span className="mt-1 block truncate font-mono text-[11px] text-[var(--text-muted)]">
                    {formatSessionTime(session)} · {formatNumber(session.danmaku_count)} 弹幕
                    {session.duration_seconds ? ' · ' + formatDuration(session.duration_seconds) : ''}
                  </span>
                </button>
                <SessionStatusBadge status={session.status} />
                <button
                  type="button"
                  onClick={() => void handleDelete(session)}
                  disabled={deletingId === session.id}
                  aria-label={'删除 ' + (session.room_title || '场次')}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[var(--text-muted)] hover:bg-[var(--alert-error-bg)] hover:text-[var(--status-error)]"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </article>
            ))}
          </div>
        </>
      )}

      {visibleCount < filteredSessions.length && (
        <div className="border-t border-[var(--border-subtle)] text-center">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setVisibleCount((count) => count + 10)}
            className="w-full rounded-none text-[var(--text-muted)]"
          >
            显示更多
            <ArrowRight className="h-3.5 w-3.5 rotate-90" />
          </Button>
        </div>
      )}
    </section>
  );
}
